/**
 * FAQModal - Categorized, searchable FAQ page
 * 
 * Organized by module with accordion-style Q&A items
 * and a search bar for quick filtering.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpVideoTour from './HelpVideoTour';

const FAQ_DATA = [
  {
    category: 'General',
    icon: '🏠',
    items: [
      {
        q: 'What is LGL Portal?',
        a: 'LGL Portal is an ERP (Enterprise Resource Planning) system that consolidates all LGL Group operations into a single platform. It covers project management, progress tracking, estimation, forecasting, material management, quality control, safety, and more.'
      },
      {
        q: 'How do I sign in?',
        a: 'LGL Portal uses Microsoft Single Sign-On (SSO). Click "Sign In" and use your company Microsoft account. You will be authenticated automatically through Azure Entra ID.'
      },
      {
        q: 'How do I switch between projects?',
        a: 'Use the Project Dropdown in the header bar. Click it to see a searchable list of all your assigned projects. Type a name or job number to filter, then click to navigate.'
      },
      {
        q: 'How do I switch departments?',
        a: 'Use the Department Dropdown in the header bar. Select the department you need (Accounting, Estimation, Procurement, etc.) and the sidebar menu will update to show that department\'s modules.'
      },
      {
        q: 'Why can\'t I see certain menu items?',
        a: 'Your access is controlled by your role and department assignments. You only see menu items that your role grants permission to. Contact your Project Manager or Administrator if you need access to additional features.'
      },
      {
        q: 'Why can\'t I see certain projects?',
        a: 'You only see projects you\'ve been assigned to. Contact your Project Manager or Admin to be added to a project.'
      },
      {
        q: 'How do I change my password?',
        a: 'LGL Portal uses Microsoft Single Sign-On. To change your password, update it through your Microsoft/company account settings (not through the portal).'
      },
      {
        q: 'What browsers are supported?',
        a: 'LGL Portal works best on Chrome, Edge, and Firefox. Safari is also supported. Internet Explorer is not supported.'
      },
      {
        q: 'How do I print a page?',
        a: 'Click the printer icon (🖨️) in the header bar, or press Ctrl+P (Cmd+P on Mac). The page will be formatted for printing automatically.'
      },
    ]
  },
  {
    category: 'Progress Tracking',
    icon: '📊',
    items: [
      {
        q: 'How do I upload progress data?',
        a: 'Go to Progress Tracking → choose your discipline (e.g., Piping). Click the "Import" button in the toolbar, drag and drop your Excel file, verify the column mapping, and click Upload. Large files are processed as background jobs.'
      },
      {
        q: 'How do I export tracker data?',
        a: 'In any tracker, click the "Export" button in the toolbar. Choose from: Dashboard Only (visible columns), All Columns, or Full Export. The Excel file will download automatically.'
      },
      {
        q: 'How do I add custom columns (UDFs)?',
        a: 'Click the gear icon (⚙️ Column Config) in the toolbar, then use the "Add UDF" option to create User Defined Fields. You can add up to 16 custom columns per project.'
      },
      {
        q: 'How do I see progress trends over time?',
        a: 'In the Piping tracker, click any Line Tag (blue hyperlink) in the first column. A modal will show a chart of progress history over time for that item.'
      },
      {
        q: 'Can I drag-fill values like Excel?',
        a: 'Yes! Select a cell with a value, grab the small square handle at the bottom-right of the cell, and drag down to fill the value into adjacent cells.'
      },
      {
        q: 'How do I filter data in the tracker?',
        a: 'Click the filter icon on any column header. You can use search text, checkboxes, or comparison operators (equals, contains, greater than, etc.). Multiple filters can be active at the same time. Click "Clear All Filters" to reset.'
      },
      {
        q: 'My upload failed. What should I do?',
        a: 'Check that your Excel file has no merged cells, headers match the expected format, and data types are correct (numbers in number columns). Try downloading the template first to see the correct format.'
      },
      {
        q: 'Why does the import template have fewer columns than the tracker?',
        a: 'The template only includes ~40 source-data columns (line info, sizing, budget, WBS codes). The database has ~68 columns total, but progress tracking columns (Shop Completion, Fit Up, Bolt/Weld, etc.) start at 0 and are updated live in the tracker. Calculated columns (Earned Hours, Field Completion, Mat Bore) are computed automatically by the database. UDF columns are managed through Column Config, and system columns (ID, timestamps, Job Number) are set automatically.'
      },
      {
        q: 'Can I undo changes?',
        a: 'Individual cell changes are auto-saved immediately and cannot be undone. For bulk changes, consider exporting a backup before making major edits.'
      },
    ]
  },
  {
    category: 'Forecasting & Reports',
    icon: '📈',
    items: [
      {
        q: 'How do I view cost performance?',
        a: 'Go to Forecasting → Dashboard for an overview of budget vs actuals. For detailed analysis, use the sub-tabs: Budget, Actuals + Accruals, PO, Invoices, Commitments, Cash Flow, and Forecast.'
      },
      {
        q: 'What do CPI and SPI mean in the EVM Report?',
        a: 'CPI (Cost Performance Index) is EV ÷ AC: > 1.0 under budget, < 1.0 over. SPI (Schedule Performance Index) is EV ÷ PV to the data date — not EV ÷ BAC. > 1.0 ahead of the plan to date, < 1.0 behind. The histogram defaults to man-weeks (hours ÷ the selected P6 schedule calendar).'
      },
      {
        q: 'How do I generate a report for the client?',
        a: 'Go to Reports → Client Report. The report loads automatically for the current project. You can then print or export it for distribution.'
      },
      {
        q: 'What is the Maestro Reports module?',
        a: 'Maestro Reports generates reports from the Maestro accounting system, including payroll reports and cost exports. It\'s used to reconcile portal data with the external accounting system.'
      },
    ]
  },
  {
    category: 'Material Management',
    icon: '📦',
    items: [
      {
        q: 'How do I track material status?',
        a: 'Go to Material Management → Material Log. Each material item has a status field (Ordered, Shipped, Received, Installed) that you can view and edit. Use filters to see outstanding items.'
      },
      {
        q: 'How do I look up tariff codes?',
        a: 'Go to Material Management → Tariff. Enter a keyword (e.g., "pipe", "valve") in the search bar. Results show the harmonized tariff code and duty rate.'
      },
      {
        q: 'What is the Currency Exchange tool?',
        a: 'It shows live CAD/USD exchange rates for cost conversions. Found under Material Management → Currency Exchange.'
      },
    ]
  },
  {
    category: 'Estimation',
    icon: '💰',
    items: [
      {
        q: 'How do I create a cost breakdown?',
        a: 'Go to Estimation → Cost Breakdown. Use the tabs (Summary, Disciplines, Indirects, Site Expenses, Rentals, Labour Rates) to enter costs. The Summary tab aggregates all sub-sections. Use Mark-up to add overhead and profit.'
      },
      {
        q: 'Is there an EVM / Resource Control page on an estimate?',
        a: 'Yes. With an estimate selected, open Bid Package → Proposal Schedule → EVM Report (next to Proposal Schedule). You will usually only have the budget / proposal curve until the job is awarded and Maestro spend exists. Man-weeks use the selected P6 XER calendar. Maestro spent dates are week-ending Saturday and plot on the next P6 Sunday (8/1 → 8/2).'
      },
      {
        q: 'What is BOM Extraction?',
        a: 'BOM (Bill of Materials) Extraction uses AI to automatically extract line items from uploaded BOM documents (PDF or Excel). Go to Estimation → Piping → BOM Extraction, upload your file, and review the extracted items.'
      },
      {
        q: 'What are Lookup Tables?',
        a: 'Lookup Tables provide reference data for estimation: Size, Material, Schedule, and Length tables. Found under Estimation → Piping → Lookup Tables.'
      },
    ]
  },
  {
    category: 'QA/QC',
    icon: '✅',
    items: [
      {
        q: 'How do I fill out an inspection form?',
        a: 'Go to QA/QC → Fillable → choose the form type (Concrete Inspection, Pipe Inspection, Pumps, etc.). Fill in all required fields and submit or save as draft.'
      },
      {
        q: 'How do I create a punch list item?',
        a: 'Go to QA/QC → Punch List / Deficiency List. Click Add to create a new item. Enter description, location, responsible party, and priority. Track items until resolution.'
      },
      {
        q: 'What is an NCR?',
        a: 'NCR stands for Non-Conformance Report. It documents when work, materials, or processes don\'t meet specifications. Go to QA/QC → Non-Conformance Report to create and track NCRs.'
      },
    ]
  },
  {
    category: 'Technical & Documentation',
    icon: '📐',
    items: [
      {
        q: 'How do I create an RFI?',
        a: 'Go to Technical → RFIs. Click "New RFI", enter the subject, question/request, assign to the responsible party, attach documents if needed, and submit. Track the response status in the RFI log.'
      },
      {
        q: 'How do I upload project photos?',
        a: 'Go to Technical → Photos. Click Upload, select your photos, add descriptions and dates. Photos are organized by date and area.'
      },
      {
        q: 'How do I create a transmittal?',
        a: 'Go to Doc\'s Management → Transmittal Log. Click "New Transmittal", enter recipient, subject, description, attach documents, and submit. The system generates a transmittal number automatically.'
      },
      {
        q: 'How do I submit a daily report?',
        a: 'Go to Technical → Daily Report. Select the date, enter weather conditions, manpower counts, activities performed, and notes. Submit the report.'
      },
    ]
  },
  {
    category: 'Safety (HSE)',
    icon: '🦺',
    items: [
      {
        q: 'How do I create a JHA?',
        a: 'Go to HSE → Job Hazard Analysis (JHA/JSA). Click "New JHA", describe the work activity, identify hazards, list control measures, and get required signatures/approvals.'
      },
      {
        q: 'How do I report an incident?',
        a: 'Go to HSE → Incident / Near-Miss Report. Click "New Report", enter date, time, location, description, persons involved, and document corrective actions.'
      },
      {
        q: 'How do I track safety certificates?',
        a: 'Go to HSE → Safety Certificates DB. View all workers\' certifications and filter by expiry date to identify upcoming renewals.'
      },
    ]
  },
  {
    category: 'Performance & Troubleshooting',
    icon: '🔧',
    items: [
      {
        q: 'Why is the page loading slowly?',
        a: 'Large datasets may take a moment to load. Try: 1) Use column filters to reduce visible data. 2) Collapse columns you don\'t need. 3) Close other browser tabs. 4) Use Chrome or Edge for best performance.'
      },
      {
        q: 'I see an error message. What should I do?',
        a: 'Try refreshing the page first. If the error persists, clear your browser cache and try again. If it still occurs, note the exact error message and contact support.'
      },
      {
        q: 'My data didn\'t save. What happened?',
        a: 'Data auto-saves when you leave a cell. If you lose internet connection during editing, changes may not save. Check your internet connection and re-enter the data.'
      },
      {
        q: 'How do I contact support?',
        a: 'Click the Help (?) button in the header and select "Contact Us & Support". You can also email support@lglgroup.com or submit a support ticket through the portal.'
      },
    ]
  },
];

const FAQModal = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [faqTab, setFaqTab] = useState('faq');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return FAQ_DATA;
    const term = searchTerm.toLowerCase();
    return FAQ_DATA
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item =>
          item.q.toLowerCase().includes(term) || item.a.toLowerCase().includes(term)
        ),
      }))
      .filter(cat => cat.items.length > 0);
  }, [searchTerm]);

  const toggleItem = (catIdx, itemIdx) => {
    const key = `${catIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isOpen) return null;

  const displayData = activeCategory !== null
    ? [filteredData[activeCategory]].filter(Boolean)
    : filteredData;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90vw', maxWidth: '900px',
        height: '85vh', maxHeight: '750px',
        backgroundColor: 'white', borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        zIndex: 10001,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        animation: 'modalSlideIn 0.3s ease',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #e5e7eb',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #dc3545 100%)',
          color: 'white', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '700' }}>
                Frequently Asked Questions
              </h1>
              <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                Quick answers to common questions about LGL Portal
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ListenButton compact guideId="faq-help" title="FAQ Help" accent="#dc3545" dark />
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)', border: 'none',
                  width: '40px', height: '40px', borderRadius: '8px',
                  cursor: 'pointer', color: 'white', fontSize: '1.5rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            </div>
          </div>

          {/* Search */}
          <div style={{ marginTop: '16px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setActiveCategory(null); }}
              style={{
                width: '100%', padding: '12px 16px 12px 44px',
                borderRadius: '8px', border: 'none',
                fontSize: '0.95rem', backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#333', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <span style={{
              position: 'absolute', left: '14px', top: '50%',
              transform: 'translateY(-50%)', fontSize: '1.1rem', opacity: 0.5,
            }}>
              🔍
            </span>
          </div>
        </div>

        {/* Top tabs — FAQ | Video Tour (last) */}
        <div style={{
          display: 'flex', flexShrink: 0, position: 'relative', zIndex: 3,
          borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
        }}>
          {[
            { id: 'faq', label: 'FAQ' },
            { id: 'tour', label: 'Video Tour' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setFaqTab(t.id)}
              style={{
                padding: '12px 20px', border: 'none', flexShrink: 0,
                borderBottom: faqTab === t.id ? '3px solid #dc3545' : '3px solid transparent',
                backgroundColor: faqTab === t.id ? 'white' : 'transparent',
                color: faqTab === t.id ? '#dc3545' : '#6b7280',
                fontWeight: faqTab === t.id ? 600 : 400,
                cursor: 'pointer', fontSize: '0.9rem', whiteSpace: 'nowrap',
                position: 'relative', zIndex: 1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Category pills */}
        {faqTab === 'faq' && (
        <div style={{
          padding: '12px 32px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', gap: '8px', flexWrap: 'wrap',
          backgroundColor: '#f9fafb', flexShrink: 0, position: 'relative', zIndex: 2,
        }}>
          <button
            onClick={() => setActiveCategory(null)}
            style={{
              padding: '6px 14px', borderRadius: '20px', border: 'none',
              fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer',
              backgroundColor: activeCategory === null ? '#dc3545' : '#e5e7eb',
              color: activeCategory === null ? 'white' : '#374151',
              transition: 'all 0.15s',
            }}
          >
            All
          </button>
          {filteredData.map((cat, catIdx) => (
            <button
              key={cat.category}
              onClick={() => setActiveCategory(activeCategory === catIdx ? null : catIdx)}
              style={{
                padding: '6px 14px', borderRadius: '20px', border: 'none',
                fontSize: '0.8rem', fontWeight: '500', cursor: 'pointer',
                backgroundColor: activeCategory === catIdx ? '#dc3545' : '#e5e7eb',
                color: activeCategory === catIdx ? 'white' : '#374151',
                transition: 'all 0.15s',
              }}
            >
              {cat.icon} {cat.category} ({cat.items.length})
            </button>
          ))}
        </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', position: 'relative', zIndex: 1 }}>
          {faqTab === 'tour' && (
            <HelpVideoTour
              guideId="faq-help"
              title="FAQ Help"
              accent="#dc3545"
              compact
              showHeader
            />
          )}
          {faqTab === 'faq' && (displayData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <p style={{ fontSize: '1.1rem' }}>No results found for "{searchTerm}"</p>
              <p style={{ fontSize: '0.9rem' }}>Try a different search term or browse all categories.</p>
            </div>
          ) : (
            displayData.map((cat, catIdx) => (
              <div key={cat.category} style={{ marginBottom: '28px' }}>
                <h3 style={{
                  margin: '0 0 12px', fontSize: '1.1rem', fontWeight: '700',
                  color: '#111827', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{cat.icon}</span>
                  {cat.category}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {cat.items.map((item, itemIdx) => {
                    const key = `${catIdx}-${itemIdx}`;
                    const isItemOpen = openItems[key];
                    return (
                      <div key={itemIdx} style={{
                        backgroundColor: isItemOpen ? '#f9fafb' : 'white',
                        border: '1px solid #e5e7eb', borderRadius: '8px',
                        overflow: 'hidden', transition: 'background-color 0.2s',
                      }}>
                        <button
                          onClick={() => toggleItem(catIdx, itemIdx)}
                          style={{
                            width: '100%', padding: '14px 18px',
                            background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', textAlign: 'left',
                          }}
                        >
                          <span style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem', flex: 1 }}>
                            {item.q}
                          </span>
                          <span style={{
                            transform: isItemOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s', color: '#6b7280',
                            fontSize: '0.8rem', marginLeft: '12px', flexShrink: 0,
                          }}>
                            ▼
                          </span>
                        </button>
                        {isItemOpen && (
                          <div style={{
                            padding: '0 18px 14px', color: '#374151',
                            lineHeight: '1.6', fontSize: '0.9rem',
                          }}>
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 32px', borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            {FAQ_DATA.reduce((sum, cat) => sum + cat.items.length, 0)} questions across {FAQ_DATA.length} categories
          </span>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px', background: '#374151', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '0.9rem',
              fontWeight: '500', cursor: 'pointer', transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#374151'}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideIn {
          from { opacity: 0; transform: translate(-50%, -48%); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
      `}</style>
    </>
  );
};

export default FAQModal;
